import React, { useState, useEffect } from 'react';

import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';

function FilePreviewModal({ fileUrl, fileName, fileContentType, onClose }) {
  const [previewError, setPreviewError] = useState('');
  const [previewType, setPreviewType] = useState('loading');
  const [previewContent, setPreviewContent] = useState('');

  useEffect(() => {
    const fetchFileForPreview = async () => {
      if (!fileUrl) {
        setPreviewError("No file URL provided for preview.");
        setPreviewType('error');
        return;
      }

      setPreviewType('loading');
      setPreviewError('');
      setPreviewContent(''); // Reset content when fetching starts

      try {
        if (fileContentType.startsWith('image/')) {
          setPreviewType('image');
          setPreviewContent(fileUrl);
        } else if (fileContentType === 'application/pdf') {
          setPreviewType('pdf');
          setPreviewContent(fileUrl);
        } else if (fileContentType === 'text/') {
          setPreviewType('text');
          setPreviewContent(fileUrl);
        } else {
          setPreviewType('unsupported');
          setPreviewContent(`No direct preview available for file type: ${fileContentType}. You can try downloading it.`);
        }
      } catch (err) {
        setPreviewError(`Failed to prepare file preview: ${err.message || 'Unknown error'}`);
        setPreviewType('error');
        console.error("Error preparing file for preview:", err);
      }
    };

    fetchFileForPreview();

    return () => {};
  }, [fileUrl, fileContentType]);


  if (!fileUrl) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h3" component="h4">File Preview: {fileName}</Typography>
          <Button onClick={onClose} color="inherit" sx={{ minWidth: 'auto', p: 1 }}>&times;</Button>
        </Box>
      </DialogTitle>
      {/* UPDATED: Added a height for the DialogContent to provide context for child's 100% height */}
      <DialogContent dividers sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px', // Existing min height
        height: '60vh', // Define a fixed height relative to viewport for content area
        maxHeight: '80vh', // Ensure it doesn't go too tall, similar to the box below
        overflowY: 'auto' // Allow content to scroll if it exceeds this height
      }}>
        {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}
        {previewType === 'loading' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
            <CircularProgress sx={{ mb: 1 }} />
            <Typography>Loading preview...</Typography>
          </Box>
        )}
        {previewType === 'image' && previewContent && (
          <Box sx={{
            width: '100%', // Take full width
            height: '100%', // Take full height of parent (DialogContent)
            display: 'flex', // Use flex to center image
            alignItems: 'center', // Center vertically
            justifyContent: 'center', // Center horizontally
            overflow: 'hidden', // Hide any potential overflow
          }}>
            <img
              src={previewContent}
              alt="File Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'block',
                objectFit: 'contain'
              }}
            />
          </Box>
        )}
        {previewType === 'pdf' && previewContent && (
          <Box sx={{ width: '100%', height: '100%' }}> {/* Changed to 100% height */}
            <iframe src={previewContent} title="File Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
          </Box>
        )}
        {previewType === 'text' && previewContent && (
          <Box sx={{
            width: '100%',
            height: '100%', // Changed to 100% height
            overflowY: 'auto',
            bgcolor: 'grey.100',
            p: 2,
            borderRadius: 1,
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
          }}>
            <Typography component="pre" variant="body2">
                <Link href={previewContent} target="_blank" rel="noopener noreferrer">
                    Click to view text file or download
                </Link>
            </Typography>
          </Box>
        )}
        {previewType === 'unsupported' && previewContent && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ mb: 2 }}>{previewContent}</Typography>
            <Button variant="contained" component={Link} href={fileUrl} download={fileName}>
              Download file
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default FilePreviewModal;